import React, { ReactElement, ReactNode, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Alert,
  Box,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { useIsMobile } from '@core/hooks/useIsMobile';
import { usePageMeta } from '@core/hooks/usePageMeta';
import type { PageMetaProps } from '@core/interfaces';
import PageHeader from './PageHeader';
import { type PageSearchConfig } from './PageSearch';
import Section, { SectionProps } from './Section';
import VerticalTabs, { TabItem } from './VerticalTabs';

interface SectionInfo {
  id: string;
  label: string;
  path: string;
  component: ReactNode;
  disabled?: boolean;
}

type SectionedPageProps = PageMetaProps & {
  action?: ReactNode;
  searchConfig?: PageSearchConfig;
  basePath: string;
  children: ReactNode;
  error?: string | null;
  onErrorDismiss?: () => void;
  contentBoxStyles?: object;
  sx?: object;
};

const SectionedPage: React.FC<SectionedPageProps> = (props) => {
  const { title, subtitle } = usePageMeta(props);
  const {
    action,
    searchConfig,
    basePath,
    children,
    error,
    onErrorDismiss,
    contentBoxStyles,
    sx = {},
  } = props;

  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();

  // Extract sections from children
  const sections = useMemo((): SectionInfo[] => {
    return React.Children.toArray(children)
      .filter(
        (child): child is ReactElement<SectionProps> =>
          React.isValidElement(child) && child.type === Section
      )
      .map((child) => ({
        id: child.props.id,
        label: child.props.label,
        path: child.props.path,
        component: child.props.children,
        disabled: child.props.disabled,
      }));
  }, [children]);

  // Auto-redirect to first section if on base path
  useEffect(() => {
    if (
      sections.length > 0 &&
      (location.pathname === basePath || location.pathname === `${basePath}/*`)
    ) {
      navigate(sections[0].path, { replace: true });
    }
  }, [sections, location.pathname, basePath, navigate]);

  // Determine active section from URL
  const activeSection = useMemo(() => {
    const currentSection = sections.find(
      (section) =>
        location.pathname === section.path ||
        location.pathname.startsWith(section.path + '/')
    );
    return currentSection?.path || sections[0]?.path || '';
  }, [sections, location.pathname]);

  // Get current section's component
  const currentSectionComponent = useMemo(() => {
    const section = sections.find((s) => s.path === activeSection);
    return section?.component || null;
  }, [sections, activeSection]);

  // Handle mobile dropdown change
  const handleMobileTabChange = (event: SelectChangeEvent) => {
    navigate(event.target.value, { replace: true });
  };

  const defaultContentBoxStyles = {
    borderRadius: '16px',
    p: 3,
    bgcolor: 'background.paper',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: 'none',
    overflow: 'auto',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    ...contentBoxStyles,
  };

  // Convert sections to TabItem format for VerticalTabs
  const tabItems = useMemo((): TabItem[] => {
    return sections.map((section) => ({
      path: section.path,
      title: section.label,
      disabled: section.disabled,
    }));
  }, [sections]);

  // Mobile dropdown component
  const MobileSectionDropdown = () => (
    <FormControl size="small" sx={{ minWidth: 200 }}>
      <Select
        value={activeSection}
        onChange={handleMobileTabChange}
        IconComponent={KeyboardArrowDown}
        sx={{
          borderRadius: '50px',
          bgcolor: 'background.paper',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '& .MuiSelect-select': {
            py: 1.5,
            px: 2,
            fontWeight: 500,
          },
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        {sections.map((section) => (
          <MenuItem
            key={section.path}
            value={section.path}
            disabled={section.disabled}
          >
            {section.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const headerAction = isMobile ? (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      {action}
      <MobileSectionDropdown />
    </Box>
  ) : (
    action
  );

  return (
    <>
      <PageHeader
        title={title}
        subtitle={subtitle}
        action={headerAction}
        searchConfig={searchConfig}
      />

      {error && (
        <Alert severity="error" onClose={onErrorDismiss} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isMobile ? (
        <Box sx={defaultContentBoxStyles}>{currentSectionComponent}</Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            flex: 1,
            minHeight: 0,
            ...sx,
          }}
        >
          <VerticalTabs
            tabs={tabItems}
            activeTab={activeSection}
            collapsed={false}
          />
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
            }}
          >
            <Box sx={defaultContentBoxStyles}>{currentSectionComponent}</Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default SectionedPage;
